import {
  GUEST_REQUEST_TRANSITIONS,
  type GuestRequest,
  type GuestRequestPaymentMethod,
  type GuestRequestPaymentStatus,
  type GuestRequestStatus,
  type GuestRequestTransition,
} from "@shared/types/guestRequest";
import {
  guestRequestIsRevenue,
  nextGuestRequestStatus,
} from "@shared/guestRequests";
import type { Money } from "@shared/types/roomOffer";
import { resolveLocalized } from "@shared/i18n";
import type { Translate } from "../../i18n/translate";
import type { Formatters } from "../../i18n/format";

/**
 * Transitions are asked of `nextGuestRequestStatus()` per verb, never listed
 * here, so a new edge shows up without a second table. Accept states its charge.
 */

/* -------------------------------------------------------------------------- */
/* Words                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The status column.
 *
 * "Accepted" and "Fulfilled" are kept apart even though both are revenue: one
 * means the hotel has taken it on, the other that the guest has had it. A queue
 * that merged them would leave reception no way to see what is still owed.
 */
export function requestStatusLabel(
  t: Translate,
  status: GuestRequestStatus
): string {
  return t(`offers:guestRequestCopy.status.${status}`);
}

/** What each verb's button says. The three the ticket names, plus the two the
 *  machine allows staff and the queue would otherwise strand. */
export function transitionLabel(
  t: Translate,
  transition: GuestRequestTransition
): string {
  return t(`offers:guestRequestCopy.transition.${transition}`);
}

/**
 * The two verbs that live behind the overflow menu.
 *
 * Accept, Decline and Mark fulfilled are the queue's job and are buttons.
 * Cancelling on a guest's behalf and refunding are rarer and heavier, and a row
 * with five buttons is a row where Decline is hard to find — which the ticket
 * calls out by name: *a staff member who cannot decline will simply ignore the
 * queue*.
 */
export const SECONDARY_TRANSITIONS: readonly GuestRequestTransition[] = [
  "cancel",
  "refund",
];

export function paymentMethodLabel(
  t: Translate,
  method: GuestRequestPaymentMethod
): string {
  return t(`offers:guestRequestCopy.paymentMethod.${method}`);
}

export function paymentStatusLabel(
  t: Translate,
  status: GuestRequestPaymentStatus
): string {
  return t(`offers:guestRequestCopy.paymentStatus.${status}`);
}

/* -------------------------------------------------------------------------- */
/* The machine, asked rather than copied                                      */
/* -------------------------------------------------------------------------- */

/**
 * Every verb this row may legally take, in the order the machine declares them.
 *
 * Asked, never tabulated — see the module docblock. `cancel` appears from both
 * `pending` and `accepted` and `refund` from both `accepted` and `completed`
 * without this file knowing either fact.
 */
export function availableTransitions(
  status: GuestRequestStatus
): GuestRequestTransition[] {
  return GUEST_REQUEST_TRANSITIONS.filter(
    (t) => nextGuestRequestStatus(status, t) !== null
  );
}

/* -------------------------------------------------------------------------- */
/* Money                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * `450 kr`. Major units, as authored — POS-2's `Money` is not minor units and
 * must not be divided by a hundred on the way to a screen.
 */
export function formatMoney(money: Money | null | undefined): string | null {
  if (!money) return null;
  return `${money.amount} ${money.currency}`;
}

/**
 * What a row is worth, or null.
 *
 * Null is a REAL answer and never an error: the amenity picker (towels,
 * refills) is a legitimate zero-revenue request, and a queue that drew it as a
 * missing price would teach reception to distrust the column. §7's own words:
 * absent means free rather than zero.
 */
export function requestAmount(request: GuestRequest): string | null {
  return formatMoney(request.price);
}

/* -------------------------------------------------------------------------- */
/* Consequences                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The sentence shown before a verb is committed.
 *
 * Accept's is the one the ticket demands: *"Detta bokför 450 kr på rum 412"* —
 * rendered here in the CMS's own language as **"This books 450 kr to room
 * 412."** Refund's is its mirror, because §13 says `refunded` exists precisely
 * so the revenue series can go DOWN, and a staff member taking money back
 * should see that they are doing so.
 *
 * Whether money is currently booked is asked of `guestRequestIsRevenue()`
 * rather than inferred from the status by eye, so the two cannot disagree.
 */
export function transitionConsequence(
  t: Translate,
  transition: GuestRequestTransition,
  request: GuestRequest,
  roomLabel: string
): string {
  const amount = requestAmount(request);
  const booked = guestRequestIsRevenue(request.status);
  const toRoom = request.payment.method === "room";
  const params = { amount, room: roomLabel };

  switch (transition) {
    case "accept":
      if (!amount)
        return t("offers:guestRequestCopy.consequence.acceptFree", params);
      return toRoom
        ? t("offers:guestRequestCopy.consequence.acceptToRoom", params)
        : t("offers:guestRequestCopy.consequence.accept", params);
    case "complete":
      return amount && booked
        ? t("offers:guestRequestCopy.consequence.completeBooked", params)
        : t("offers:guestRequestCopy.consequence.complete");
    case "decline":
      return amount
        ? t("offers:guestRequestCopy.consequence.declinePriced", params)
        : t("offers:guestRequestCopy.consequence.decline");
    case "cancel":
      return amount && booked
        ? t("offers:guestRequestCopy.consequence.cancelBooked", params)
        : t("offers:guestRequestCopy.consequence.cancel");
    case "refund":
      return amount
        ? t("offers:guestRequestCopy.consequence.refundPriced", params)
        : t("offers:guestRequestCopy.consequence.refund", params);
  }
}

/** The dialog's own title. Accept names the money because Accept IS the money. */
export function transitionTitle(
  t: Translate,
  transition: GuestRequestTransition,
  request: GuestRequest
): string {
  if (transition === "accept") {
    return requestAmount(request)
      ? t("offers:guestRequestCopy.title.acceptAndBook")
      : t("offers:guestRequestCopy.title.accept");
  }
  return t("offers:guestRequestCopy.title.transition", {
    label: transitionLabel(t, transition),
  });
}

/**
 * Whether this verb owes the guest a sentence.
 *
 * Decline is the one the ticket names, but a staff cancellation and a refund
 * arrive on the guest's television just as unexplained, so they get the same
 * optional box. Accept and complete are good news and need none.
 */
export function transitionTakesReason(
  transition: GuestRequestTransition
): boolean {
  return transition !== "accept" && transition !== "complete";
}

/* -------------------------------------------------------------------------- */
/* Clocks                                                                     */
/* -------------------------------------------------------------------------- */

/** Past this, a waiting request is worth noticing. */
export const WAIT_WARN_MS = 10 * 60_000;
/** Past this, it is late. */
export const WAIT_LATE_MS = 30 * 60_000;

/**
 * How long this has been waiting, against a clock the caller keeps moving.
 *
 * `now` is a parameter rather than a `Date.now()` inside, because the whole
 * point is that the label AGES: the component ticks a second-resolution clock
 * into state, and a queue whose "4 min ago" is frozen until somebody reloads is
 * a queue nobody trusts.
 */
export function waitedLabel(t: Translate, iso: string, now: number): string {
  const ms = Math.max(0, now - new Date(iso).getTime());
  const secs = Math.floor(ms / 1000);
  if (secs < 60)
    return t("offers:guestRequestCopy.waited.seconds", { count: secs });
  const mins = Math.floor(secs / 60);
  if (mins < 60)
    return t("offers:guestRequestCopy.waited.minutes", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return t("offers:guestRequestCopy.waited.hoursMinutes", {
      hours: hrs,
      minutes: mins % 60,
    });
  }
  return t("offers:guestRequestCopy.waited.daysHours", {
    days: Math.floor(hrs / 24),
    hours: hrs % 24,
  });
}

/**
 * A booked hour, as the wall clock it is.
 *
 * `slotStart` is a LOCAL wall clock (`2026-08-20T19:00`) and never an instant —
 * §4's seat key. So it is split rather than parsed into a `Date` in the
 * browser's zone: a CMS open in another country must show the hotel's 19:00,
 * not its own idea of when 19:00 was.
 */
export function formatSlot(
  t: Translate,
  fmt: Formatters,
  slotStart: string
): string {
  const [ymd, hm = ""] = slotStart.split("T");
  const day = new Date(`${ymd}T12:00:00Z`);
  if (Number.isNaN(day.getTime())) return slotStart;
  const date = fmt.date(day, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
  return hm
    ? t("offers:guestRequestCopy.slotAt", { date, time: hm.slice(0, 5) })
    : date;
}

/**
 * What the guest picked, as one line. Quantities included; prices are not —
 * the amount column already carries the total the guest was shown.
 *
 * The labels are the SNAPSHOT taken when the guest pressed Confirm, so a hotel
 * that renamed the dish this morning still sees what was actually ordered.
 * Resolved with a fallback rather than read strictly: a queue is not an editor,
 * and a blank line where a Swedish label exists but an English one does not
 * would be a row nobody can fulfil.
 */
export function selectionLine(
  t: Translate,
  request: GuestRequest,
  locale: string
): string {
  return request.selection
    .map((line) => {
      const label =
        resolveLocalized(line.label, locale, locale) ||
        t("offers:guestRequestCopy.itemFallback");
      return line.quantity > 1
        ? t("offers:guestRequestCopy.selectionLine", {
            quantity: line.quantity,
            label,
          })
        : label;
    })
    .join(", ");
}

/** Total things picked. Shown for orders, where "how many" is the job. */
export function selectionQuantity(request: GuestRequest): number {
  return request.selection.reduce((n, line) => n + (line.quantity || 0), 0);
}

/**
 * The three shapes a row can be, decided once.
 *
 * `asap` is a booking with no hour — the "call for staff" flow — and it is a
 * shape of its own rather than a booking with a missing field: there is no slot
 * to show and no party to seat, and drawing an empty cell for each would read
 * as data that failed to load rather than as a request that never had any.
 */
export type RequestShape = "order" | "booking" | "asap";

export function requestShape(request: GuestRequest): RequestShape {
  if (request.kind === "order") return "order";
  return request.slotStart ? "booking" : "asap";
}

/** The last thing anybody said about this row, if they said anything. */
export function latestReason(request: GuestRequest): string | null {
  for (let i = request.stamps.length - 1; i >= 0; i--) {
    const reason = request.stamps[i].reason;
    if (reason) return reason;
  }
  return null;
}

/** Who moved it last, for the audit line under a settled row. */
export function latestActor(request: GuestRequest): string | null {
  const last = request.stamps[request.stamps.length - 1];
  return last ? last.by : null;
}
